import { createSelector, createSlice } from "@reduxjs/toolkit";
import { dataHookFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { logout } from "../common/reducers/initContext";
import { markFetched } from "../common/hooks/initHook";

const key = announcement => announcement.id

export const announcementSlice = createSlice({
    name: 'announcements',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = ensureArray(action.payload).reduce((obj, announcement) => { obj[key(announcement)] = announcement; return obj; }, {});
        },
        addOrUpdate: (state, action) => {  
            ensureArray(action.payload).forEach(a => state.data[key(a)] = a)
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(a => delete state.data[key(a)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => announcementSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'announcement_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'announcement_one');

const fetchAnnouncements = network => network.get('/api/v1/announcements', announcementSlice.actions.set, visualizeFailure('get'));

export const createAnnouncement = (network, announcement) => network.put('/api/v1/announcement', announcement, visualizeSuccess('create', announcementSlice.actions.addOrUpdate), visualizeFailure('create'));
export const updateAnnouncement = (network, delta) => network.post('/api/v1/announcement', delta, visualizeSuccess('update', announcementSlice.actions.addOrUpdate), visualizeFailure('update'));
export const removeAnnouncement = (network, id) => network.delete('/api/v1/announcement/' + encodeURIComponent(id), visualizeSuccess('remove', announcementSlice.actions.remove), visualizeFailure('remove'));
// #endregion

// #region selectors
const selectAnnouncementData = state => state.announcements.data;
const selectAnnouncement = id => createSelector([selectAnnouncementData], announcements => announcements[id]);
const selectAnnouncements = createSelector([selectAnnouncementData], announcements => Object.values(announcements));
// #endregion

const listen = {
    announcementCreated: (announcement, { dispatch, websocket }) => {
        markFetched(websocket, 'announcement', key(announcement))
        dispatch(announcementSlice.actions.addOrUpdate(announcement))
    },
    announcementUpdated: (announcement, { dispatch }) => dispatch(announcementSlice.actions.addOrUpdate(announcement)),
    announcementRemoved: (announcement, { dispatch }) => dispatch(announcementSlice.actions.remove(announcement))
}

const initAnnouncements = { run: fetchAnnouncements, listen, immortal: false};
const cleanup = { root: announcementSlice.name, all: state => Object.values(selectAnnouncementData(state)), removeAll: announcementSlice.actions.remove, key: announcement => announcement.id };

export const [useAnnouncement] = dataHookFactory(selectAnnouncement, id => initAnnouncements, { cleanup });
export const [useAnnouncements] = dataHookFactory(selectAnnouncements, initAnnouncements, { cleanup });

const fullRoles = ['administrator', 'equipment manager', 'event manager', 'rostering manager', 'recurring rostering manager', 'training manager', 'template manager', 'member manager']
const readRoles = ['auditor', 'event auditor', 'template auditor', 'training auditor', 'equipment auditor', 'event viewer', 'rostering auditor', 'recurring rostering auditor', 'member auditor'];
export const canUpdateAnnouncement = (credentials, announcement) => checkPermissions(credentials, announcement, fullRoles);
export const canCreateAnnouncement = canUpdateAnnouncement;
export const canRemoveAnnouncement = canUpdateAnnouncement;
export const canViewAnnouncement = (credentials, announcement) => checkPermissions(credentials, announcement, fullRoles.concat(readRoles));

export const canRouteAnnouncements = ({credentials}) => canViewAnnouncement(credentials);

const checkPermissions = (credentials, announcement, requiredRoles) => {
    return credentials.hasRole(requiredRoles, {office: announcement?.office});
}

const reducer = { name: announcementSlice.name, reducer: announcementSlice.reducer };
export default reducer;